/**
 * A live queue feed — QA report §5.3.
 *
 * WebSocket first (Backend/lib/liveSocket.js pushes a full snapshot on every
 * change); when two attempts in a row fail — a proxy that will not upgrade, a
 * hospital firewall — Server-Sent Events for the public pages, which have a
 * stream, and plain polling for everything else. The socket keeps being
 * retried with a growing pause, and wins back the moment it connects.
 *
 * Every path delivers the same snapshot, so a screen that silently fell back
 * shows the same thing, only a little later.
 *
 * @param {object}   options
 * @param {'display'|'ticket'|'staff'} options.kind
 * @param {string}   [options.token]   the display's or the ticket's token
 * @param {() => Promise<any>} options.fetchSnapshot   the polling fallback
 * @param {string}   [options.ssePath] API path of the SSE stream, public pages only
 * @param {(data: any) => any} [options.select]  picks the snapshot out of a poll
 * @param {number}   [options.pollMs]
 * @param {boolean}  [options.enabled]
 * @returns {{ data: any, status: 'connecting'|'live'|'fallback'|'gone'|'offline', error: Error|null, refresh: () => void }}
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL, tokenStore } from '../api/client';

const BACKOFF_MS = [1000, 2000, 5000, 10000, 20000, 30000];

function socketUrl(query) {
  const base = /^https?:/.test(API_BASE_URL)
    ? API_BASE_URL.replace(/^http/, 'ws')
    : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${API_BASE_URL}`;
  return `${base}/live/ws?${query}`;
}

export function useLiveChannel({ kind, token = null, fetchSnapshot, ssePath = null, select = (d) => d, pollMs = 8000, enabled = true }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const latest = useRef({ fetchSnapshot, select });
  latest.current = { fetchSnapshot, select };

  const refresh = useCallback(async () => {
    try {
      const snapshot = await latest.current.fetchSnapshot();
      setData(latest.current.select(snapshot));
      setError(null);
    } catch (err) {
      setError(err);
      if (err && (err.status === 404 || err.status === 410)) setStatus('gone');
    }
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    let stopped = false;
    let ws = null;
    let source = null;
    let poller = null;
    let retry = null;
    let failures = 0;

    const onMessage = (raw) => {
      let message;
      try {
        message = JSON.parse(raw);
      } catch {
        return;
      }
      if (message.type === 'gone') {
        setStatus('gone');
        return;
      }
      if (message.data) {
        setData(message.data);
        setError(null);
      }
    };

    const stopFallback = () => {
      if (source) source.close();
      source = null;
      clearInterval(poller);
      poller = null;
    };

    const startFallback = () => {
      if (stopped || source || poller) return;
      setStatus('fallback');
      if (ssePath && typeof EventSource !== 'undefined') {
        source = new EventSource(`${API_BASE_URL}${ssePath}`);
        source.onmessage = (event) => onMessage(event.data);
        source.onerror = () => {
          // EventSource reconnects on its own; if it cannot, poll instead.
          if (source && source.readyState === EventSource.CLOSED) {
            source = null;
            poller = setInterval(refresh, pollMs);
          }
        };
      } else {
        poller = setInterval(refresh, pollMs);
      }
    };

    const connect = () => {
      if (stopped) return;
      const query = kind === 'staff' ? 'staff=1' : `${kind}=${encodeURIComponent(token)}`;
      try {
        ws = new WebSocket(socketUrl(query));
      } catch {
        ws = null;
      }
      if (!ws) {
        failures += 1;
        startFallback();
        return;
      }
      ws.onopen = () => {
        // A desk proves who it is in its first frame, never in the URL.
        if (kind === 'staff') ws.send(JSON.stringify({ auth: tokenStore.get() }));
        failures = 0;
        stopFallback();
        setStatus('live');
      };
      ws.onmessage = (event) => onMessage(event.data);
      ws.onclose = (event) => {
        ws = null;
        if (stopped) return;
        if (event.code === 4404) {
          setStatus('gone');
          return;
        }
        if (event.code === 4401 || event.code === 4403) {
          // Not allowed to stream: polling will say why, through the API.
          startFallback();
          return;
        }
        failures += 1;
        if (failures >= 2) startFallback();
        else setStatus('connecting');
        retry = setTimeout(connect, BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)]);
      };
    };

    refresh();
    connect();

    const online = () => {
      if (!ws && !stopped) {
        clearTimeout(retry);
        connect();
      }
    };
    window.addEventListener('online', online);

    return () => {
      stopped = true;
      window.removeEventListener('online', online);
      clearTimeout(retry);
      stopFallback();
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
    };
  }, [kind, token, ssePath, pollMs, enabled, refresh]);

  return { data, status, error, refresh };
}

export default useLiveChannel;
